import React from 'react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'

import { ChevronDownIcon } from '../../icons/ChevronDownIcon'
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon'
import { ChevronRightIcon } from '../../icons/ChevronRightIcon'
import { AlertIconWrapper } from '../../ui/AlertIconWrapper/AlertIconWrapper'
import { Truncate } from '../../ui/Truncate/Truncate'
import { TextSmall } from '../../ui/Typography/Typography'
import { InfoIconWrapper } from '../InfoIconWrapper/InfoIconWrapper'
import { useMenu } from '../context/MenuContext'
import { useArabicSessionLanguage } from '../context/SessionSettingsContext'
import { MenuSection } from './menuSections/useMenuSections'

import styles from './MenuLink.module.scss'

type MenuLinkProps = {
  section: MenuSection
  isActive?: boolean
  onClick?: (e: React.MouseEvent) => void
  isTopLevel?: boolean
  onExpandClick?: (e: React.MouseEvent) => void
  infoClick?: () => void
  isScreenMenu?: boolean
}

const iconVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

export const MenuLink: React.FC<MenuLinkProps> = (props) => {
  const isArabic = useArabicSessionLanguage()
  const { isActive, isTopLevel, isScreenMenu, section } = props
  const { onClick, onExpandClick, infoClick } = props

  const {
    icon: Icon,
    rightIcon: RightIcon,
    label,
    highlighted,
    alertActive,
    isSelected,
    children,
    screenChildren,
  } = section

  const isExpandable = (children?.length ?? 0) > 0
  const { isMenuOpen, expandedSections } = useMenu()
  const isExpanded = isExpandable && expandedSections.includes(section.id)

  const handleExpandClick = (e: React.MouseEvent) => {
    if (onExpandClick) {
      e.preventDefault()
      e.stopPropagation()
      onExpandClick(e)
    }
  }

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      className={classNames(styles.menuLink, {
        [styles.active]: isExpanded || isActive,
        [styles.isSelected]: isSelected,
        [styles.isScreenMenu]: isScreenMenu,
        [styles.expandable]: isExpandable,
        [styles.leftSpacing]: !Icon,
        [styles.menuClosed]: !isMenuOpen,
        [styles.highlighted]: highlighted,
        [styles.topLevel]: isTopLevel,
      })}
      onClick={onClick}
    >
      {Icon && !isScreenMenu && (
        <AlertIconWrapper
          isActive={!!section.children?.some((section) => section.alertActive)}
          spaced
          color='success'
        >
          <span className={styles.leftIcon}>
            <Icon size={24} color={'foregroundInverse'} />
          </span>
        </AlertIconWrapper>
      )}
      {isScreenMenu && (
        <span className={styles.leftIcon}>
          {isArabic ? (
            <ChevronRightIcon color={'foregroundInverse'} size={24} />
          ) : (
            <ChevronLeftIcon color={'foregroundInverse'} size={24} />
          )}
        </span>
      )}

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <AlertIconWrapper isActive={alertActive} spaced color='success'>
              <TextSmall className={styles.label}>
                <Truncate>{label}</Truncate>
              </TextSmall>
            </AlertIconWrapper>

            {infoClick && <InfoIconWrapper onClick={infoClick} />}

            {screenChildren && !isScreenMenu && (
              <motion.span
                key='screenChildren'
                className={styles.rightIcon}
                variants={iconVariants}
                initial='hidden'
                animate='visible'
                exit='exit'
                transition={{ duration: 0.2 }}
                onClick={onClick}
              >
                {isArabic ? (
                  <ChevronLeftIcon size={24} color={'foregroundInverse'} />
                ) : (
                  <ChevronRightIcon size={24} color={'foregroundInverse'} />
                )}
              </motion.span>
            )}

            {isExpandable && !isScreenMenu && (
              <motion.span
                key='expandable'
                className={styles.rightIcon}
                variants={iconVariants}
                initial='hidden'
                animate='visible'
                exit='exit'
                transition={{ duration: 0.2 }}
                onClick={handleExpandClick}
              >
                <ChevronDownIcon
                  className={classNames(styles.chevron, {
                    [styles.expanded]: isExpanded,
                  })}
                  color={'foregroundInverse'}
                />
              </motion.span>
            )}

            {RightIcon && !isScreenMenu && (
              <motion.span
                key='rightIcon'
                className={styles.rightIcon}
                variants={iconVariants}
                initial='hidden'
                animate='visible'
                exit='exit'
                transition={{ duration: 0.2 }}
              >
                <RightIcon color='alwaysWhite' size={24} />
              </motion.span>
            )}
          </>
        )}
      </AnimatePresence>
    </motion.button>
  )
}
